<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col md="9">
          <validation-observer ref="batchBackdatingRules">
            <b-form>
              <b-row>
                <b-col
                  md="5"
                  class="mb-2"
                >
                  <div>
                    {{ $t('Select file (.xlsx format only)') }}
                  </div>
                  <b-form-file
                    id="file-name"
                    v-model="hasFileData"
                    :label="$t('Select file (.xlsx format only)')"
                    :state="Boolean(hasFileData)"
                    :placeholder="$t('Choose a file or drop it here...')"
                    :drop-placeholder="$t('Drop file here...')"
                    error-name="file"
                    rules="required"
                    accept=".xlsx"
                    @change="onChangeXLSX($event)"
                  />
                </b-col>
                <b-col md="3">
                  <DatePickerField
                    id="financial-date"
                    v-model="financialBackdateTo"
                    rules="required"
                    :min-date="new Date()"
                    :label="$t('Financial Date')"
                    :error-name="$t('Financial Date')"
                    :config="datePickerConfig"
                    :placeholder="$t('Financial Date')"
                  />
                </b-col>
                <b-col
                  md="2"
                  class="mt-2"
                >
                  <SwitchButton
                    v-model="enablePreview"
                    :label="$t('Enable Preview?')"
                    name="enable-preview"
                  />
                </b-col>
                <b-col md="2">
                  <Button
                    v-if="getUserPermission('BACKDATING_POSTING') && parseExcelFile.length"
                    class="mt-1"
                    :label="$t('Next >')"
                    size="md"
                    :hidden="isNextValidateHidden"
                    :loading="isLoading"
                    @click="handleNextValidate"
                  />
                  <Button
                    v-if="getUserPermission('BACKDATING_POSTING') && !errorMessages.length"
                    :label="$t('Create Backdating Posting')"
                    size="md"
                    :hidden="isCreateBatchHidden"
                    :disabled="isDisabled"
                    :loading="isLoading"
                    @click="handleSubmit"
                  />
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col md="3">
          <Button
            class="ml-lg-2"
            align="end"
            :label="$t('Download Batch Backdating Posting Format')"
            size="md"
            icon="DownloadIcon"
            show-icon
            :loading="isLoading2"
            :disabled="isDisabled2"
            @click="handleDownloadSubmit"
          />
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="backdatingPostingSuccessCountReport"
      :items="errorMessages"
      responsive
      :fields="translatedErrorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <p
      v-if="totalSheetRows && !enablePreview"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
    <b-table
      v-if="enablePreview"
      ref="customerInfoRef"
      :items="batchCustomerPostingList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(debit)="data">
        <p class="mb-0">
          {{ data.value ? toBalance(data.value) : '' }}
        </p>
      </template>
      <template #cell(credit)="data">
        <p class="mb-0">
          {{ data.value ? toBalance(data.value) : '' }}
        </p>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      name="table-pagination"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import TablePagination from '@/views/components/table/TablePagination.vue'
import Button from '../../components/button/Button.vue'
import { toKobo, toBalance } from '@/helpers/formatAmount'
import SwitchButton from '../../components/switch/Switch.vue'
import DatePickerField from '../../forms/DatePickerField.vue'
import { postingPermissions } from '@/helpers/permissions'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    SwitchButton,
    DatePickerField,
    TablePagination,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      totalSheetRows: '',
      enablePreview: true,
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      batchCustomerPostingList: [],
      validatedExcelFileData: [],
      sortOrders: {},
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number (GL Code or CASA Number)' },
        { key: 'branchCode', sortable: true, label: 'Branch Code' },
        { key: 'branchName', sortable: true, label: 'Branch Name' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'narration', sortable: true, label: 'Transaction Details or Narration' },
        { key: 'casaNumberSource', sortable: true, label: 'CASA Number Source' },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      isCreateBatchHidden: true,
      isNextValidateHidden: false,
      sortDesc: false,
      sortBy: 'accountNumber',
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: '',
        defaultDate: '',
      },
      financialBackdateTo: null,
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  computed: {
    totalCredit() {
      return this.parseExcelFile.reduce((acc, item) => acc + Number(item.credit), 0)
    },
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedErrorHeaders() {
      return this.errorHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      validateBatchGlPostingCreation: 'posting/validateBatchGlPostingCreation',
      batchBackdatingPosting: 'posting/postingBatchPostingAction',
      downloadAccountFormat: 'batch/downloadAccountFormat',
    }),
    async handleNextValidate() {
      try {
        const postingApprovalModel = {
          amount: toKobo(this.totalCredit),
          initiatedBy: '',
          approvedBy: '',
          postingType: 'BatchGLandCustomerBackdatingPosting',
        }
        this.isLoading = true
        this.hasFileData = []
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          casaNumberSource: Number(item.casaNumberSource),
          debit: toKobo(item.debit),
          credit: toKobo(item.credit),
        }))
        const permission = postingPermissions.VIEW_BATCH_BACKDATING_POSTING
        const response = await this.validateBatchGlPostingCreation({
          payload: formData, financialBackdateTo: this.financialBackdateTo, initiatedBy: this.userDetails.email, postingApprovalModel, permission,
        })
        const { status, data } = response
        if (status === 200 && data) {
          this.batchCustomerPostingList = data.payload
          this.isNextValidateHidden = true
          this.alertSuccess({ text: this.$t('Everything seems good! Continue with Batch Backdating posting') })
          this.isCreateBatchHidden = false
          this.isDisabled = false
          this.isLoading = false
        }
      } catch (error) {
        this.handleError(error)
        this.isNextValidateHidden = false
        this.isCreateBatchHidden = true
        this.isDisabled = true
        this.isLoading = false
      } finally {
        this.isLoading = false
      }
    },
    async handleSubmit() {
      this.$refs.batchBackdatingRules.validate().then(success => {
        if (success) {
          this.confirm(this.batchBackdingPostingAction, { text: this.$t('Are you sure you want to Create Batch Backdating Posting?') })
        }
      })
    },
    async batchBackdingPostingAction() {
      try {
        const postingApprovalModel = {
          amount: toKobo(this.totalCredit),
          initiatedBy: '',
          approvedBy: '',
          postingType: 'BatchGLandCustomerBackdatingPosting',
        }
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.batchCustomerPostingList.map(item => ({
          ...item,
        }))
        const permission = postingPermissions.BATCH_BACKDATING_POSTING
        const response = await this.batchBackdatingPosting({
          payload: formData, financialBackdateTo: this.financialBackdateTo, initiatedBy: this.userDetails.email, postingApprovalModel, permission,
        })
        const { status, data } = response
        if (status === 200 && data.isSuccessful) {
          this.hasFileData = []
          this.parseExcelFile = []
          const text = this.$t('Your Batch Backdating Posting request was successful. Backdating Posting reference ID is')
          this.alertSuccess({ text: `${text} ${data.referenceNumber}` })
        } else if (status === 200 && !data.isSuccessful) {
          this.alertError({ text: data.message })
          const errorText = this.$t('Your Batch Backdating Posting request failed. Reason')
          this.alertError({ text: `${errorText}: ${data.message}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const errorText = this.$t('Your Batch Backdating Posting request failed. Reason')
          this.alertError({ text: `${errorText}: ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.errorMessages = []
      this.isCreateBatchHidden = true
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'mm/dd/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            accountNumber: items['Account Number(GL or Customer)'],
            branchCode: items['Branch Code'],
            debit: items['Debit Amount'] === undefined ? 0 : items['Debit Amount'],
            credit: items['Credit Amount'] === undefined ? 0 : items['Credit Amount'],
            instrumentNumber: items['Instrument Number'],
            entryCode: items['Sort Code'],
            narration: items['Transaction Details or Narration'],
            casaNumberSource: items['CASA Number Source'] === undefined ? 0 : items['CASA Number Source'],
          }))
          this.handleNextValidate()
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Batch Backdating Posting Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const BatchAccountFileTemplate = 18
        const response = await this.downloadAccountFormat(BatchAccountFileTemplate)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Batch-Account-Creation.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download Batch Backdating Posting Format was successful') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to download Batch Backdating Posting Format failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
